import type { GlobalAccessibilityLabels as GlobalAccessibilityLabelsSchema } from '../../../amplienceTypes/schemas/imported/global-accessibility-labels-schema';
import type { MouseEvent, ReactElement } from 'react';

import { memo, useCallback } from 'react';
import styled, { css } from 'styled-components';

import { AUDIO_EYE_LAUNCHER_SELECTOR, useIsAudioEyeReady } from './useIsAudioEyeReady';
import { a11yInvisibleStyle } from '../common/styles';
import { FesLink } from '../core/link/FesLink';
import { useStaticContext } from '~/utilities/context/static/StaticContext';

const skipLinkStyle = css`
  ${a11yInvisibleStyle}

  &:focus-visible {
    position: fixed;
    left: 0;
    top: 0;
    z-index: 10000;
    width: 100%;
    padding: 1rem;
    background-color: #fff;
    clip: unset;
    text-align: center;
  }
`;

const S = {
  Label: styled(FesLink)`
    ${skipLinkStyle}
  `,
};

const CONTACT_PAGE_HREF = '/customer-service/contact';
const AUDIO_EYE_APP_ID = 'ae_app';
const MAIN_CONTENT_ID = 'container';

const triggerAudioEyeLauncher = (launcher: HTMLElement): void => {
  launcher.focus();
  launcher.click();
};

const AccessibilityLabelsComponent = ({
  accessibilityLabel1,
  accessibilityLabel2,
  openAccessibilityTools = 'Press enter to open the accessibility tools',
}: GlobalAccessibilityLabelsSchema): ReactElement => {
  const { configuration } = useStaticContext();
  const isAudioEyeReady = useIsAudioEyeReady(configuration.enableAudioEye ?? false);

  const handleOpenAccessibilityTools = useCallback((event: MouseEvent<HTMLAnchorElement>): void => {
    const launcher = document.querySelector(AUDIO_EYE_LAUNCHER_SELECTOR);

    if (!(launcher instanceof HTMLElement)) {
      return;
    }

    event.preventDefault();
    triggerAudioEyeLauncher(launcher);
  }, []);

  return (
    <>
      <S.Label href={`#${MAIN_CONTENT_ID}`}>{accessibilityLabel2}</S.Label>
      {isAudioEyeReady && openAccessibilityTools && (
        <S.Label href={`#${AUDIO_EYE_APP_ID}`} onClick={handleOpenAccessibilityTools}>
          {openAccessibilityTools}
        </S.Label>
      )}
      <S.Label href={CONTACT_PAGE_HREF}>{accessibilityLabel1}</S.Label>
    </>
  );
};

export const AccessibilityLabels = memo(AccessibilityLabelsComponent);
